import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import {
  Popover,
  PopoverAnchor,
  PopoverArrow,
  PopoverBareContent,
} from '@nucleus-commerce/ui/components/popover';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import {
  ARROW_HEIGHT,
  ARROW_WIDTH,
  placeCard,
  spotlightBox,
  VIEWPORT_MARGIN,
  type Box,
  type Side,
  type Size,
} from '../engine/geometry';
import { stepKeys, tourFinishKey, tourTitleKey } from '../registry';
import { useTour } from '../tour-context';
import type { Placement } from '../types';

import { PausedPill } from './paused-pill';
import { StepCard } from './step-card';
import { useTrackedRect, useViewport } from './use-tracked-rect';

const FOCUSABLE = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
const TEXT_INPUT = 'input, textarea, select, [contenteditable="true"]';
const LAYER_Z = 'z-[70]';
const CARD_Z = 'z-[71]';

const SPOTLIGHT_MOTION =
  'transition-[top,left,width,height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none';

/** Until the card has been measured; close to an explain card with two lines of body. */
const CARD_ESTIMATE: Size = { width: 320, height: 200 };
/** Keeps the arrow off the card's rounded corners. */
const ARROW_PADDING = 14;

function preferredSide(placement: Placement | undefined): Side | undefined {
  return placement && placement !== 'auto' ? placement : undefined;
}

function sameSize(a: Size, b: Size) {
  return a.width === b.width && a.height === b.height;
}

/** Click-swallowing panes around the hole, so only the target takes clicks on a try-it step. */
function Blockers({ hole }: { hole: Box }) {
  const right = hole.left + hole.width;
  const bottom = hole.top + hole.height;
  const pane = cn('pointer-events-auto fixed', LAYER_Z);
  return (
    <>
      <div
        aria-hidden
        className={pane}
        style={{ top: 0, left: 0, right: 0, height: Math.max(0, hole.top) }}
      />
      <div aria-hidden className={pane} style={{ top: bottom, left: 0, right: 0, bottom: 0 }} />
      <div
        aria-hidden
        className={pane}
        style={{ top: hole.top, left: 0, width: Math.max(0, hole.left), height: hole.height }}
      />
      <div
        aria-hidden
        className={pane}
        style={{ top: hole.top, left: right, right: 0, height: hole.height }}
      />
    </>
  );
}

/**
 * Everything a running tour draws: the dimmed backdrop with the spotlight cut-out, the step
 * card (anchored, centred or a bottom sheet), the paused pill and the polite live region.
 */
export function TourLayer() {
  const tour = useTour();
  const { t } = useTranslation('common');
  const { t: tt } = useTranslation(tour.namespace);
  const { state, element, isMobile, reducedMotion } = tour;
  const box = useTrackedRect(state.phase === 'showing' && state.target ? element : null);
  const viewport = useViewport();
  const cardRef = useRef<HTMLDivElement>(null);

  const step = state.phase === 'showing' ? state.steps[state.index] : undefined;
  const definition = state.tourId ? tour.registry.get(state.tourId) : undefined;
  const counted = state.steps.filter((s) => s.kind !== 'finish').length;
  const anchored = Boolean(
    step &&
    element &&
    box &&
    state.target &&
    !state.missing &&
    step.kind !== 'intro' &&
    step.kind !== 'finish',
  );
  const container = anchored
    ? (element?.closest<HTMLElement>('[role="dialog"], [role="menu"]') ?? null)
    : null;

  const values = tour.values;
  const title =
    step && definition
      ? step.kind === 'finish'
        ? t('tour.finishTitle', { title: tt(tourTitleKey(definition.id), values) })
        : tt(stepKeys(definition.id, step).title, values)
      : '';
  const body =
    step && definition
      ? step.kind === 'finish'
        ? tt(tourFinishKey(definition.id), values)
        : tt(stepKeys(definition.id, step, state.copy).body, values)
      : '';

  const related = useMemo(
    () =>
      (definition?.related ?? []).flatMap((id) => {
        const related = tour.registry.get(id);
        return related && tour.isAvailable(related)
          ? [{ id, title: tt(tourTitleKey(id), values) }]
          : [];
      }),
    [definition, tour, tt, values],
  );

  const { announce } = tour;
  useEffect(() => {
    if (!step) return;
    if (step.kind === 'action' && !state.missing) {
      announce(t('tour.yourTurnAnnounce', { title }));
    } else if (step.kind === 'finish') {
      announce(title);
    } else {
      announce(t('tour.stepAnnounce', { current: state.index + 1, total: counted, title }));
    }
  }, [announce, counted, state.index, state.missing, step, t, title]);

  // An anchored step waits one frame for its box, so the card never jumps from the centre.
  const waitingForBox = Boolean(state.target && !box && !state.missing);
  const cardShown = Boolean(step && definition && !waitingForBox);

  // Move focus into the card once it is on screen.
  useEffect(() => {
    if (!cardShown) return;
    const id = window.setTimeout(() => {
      cardRef.current?.querySelector<HTMLElement>('h2')?.focus({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(id);
  }, [cardShown, state.index, state.tourId]);

  // Keyboard: Esc ends the tour from anywhere. On the card, arrows and Enter move (never while
  // typing in a field); Tab stays inside a modal card, and a try-it card shares Tab with its
  // target only.
  const { end, next, back } = tour;
  const isActionStep = step?.kind === 'action' && !state.missing;
  const canGoBack = state.history.length > 0;
  useEffect(() => {
    if (state.phase !== 'showing') return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (!event.defaultPrevented) end('escape');
        return;
      }
      const card = cardRef.current;
      const from = event.target instanceof HTMLElement ? event.target : null;
      if (!card || !from) return;
      if (event.key === 'Tab' && isActionStep && element?.contains(from)) {
        const first = card.querySelector<HTMLElement>(FOCUSABLE);
        if (first) {
          event.preventDefault();
          first.focus();
        }
        return;
      }
      if (!card.contains(from) || from.closest(TEXT_INPUT)) return;
      if (event.key === 'ArrowRight' && !isActionStep) {
        event.preventDefault();
        next();
      } else if (event.key === 'ArrowLeft' && canGoBack) {
        event.preventDefault();
        back();
      } else if (event.key === 'Enter' && !isActionStep && !from.closest('button, a')) {
        event.preventDefault();
        next();
      } else if (event.key === 'Tab') {
        const items = [...card.querySelectorAll<HTMLElement>(FOCUSABLE)];
        const first = items[0];
        const last = items[items.length - 1];
        if (!first || !last) return;
        if (!event.shiftKey && from === last) {
          event.preventDefault();
          if (isActionStep && element) element.focus();
          else first.focus();
        } else if (event.shiftKey && (from === first || !items.includes(from))) {
          event.preventDefault();
          last.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [back, canGoBack, element, end, isActionStep, next, state.phase]);

  // The spotlight and the card's anchor are the same box, so the card can never point anywhere
  // the spotlight is not.
  const hole = anchored && box ? spotlightBox(box, viewport) : null;
  const holeRef = useRef<Box | null>(hole);
  useLayoutEffect(() => {
    holeRef.current = hole;
  });
  const virtualRef = useRef({
    getBoundingClientRect: () => {
      const current = holeRef.current;
      return current
        ? new DOMRect(current.left, current.top, current.width, current.height)
        : new DOMRect();
    },
  });

  // The popover portal mounts the card a render later than the layer, hence a node in state.
  const [cardNode, setCardNode] = useState<HTMLDivElement | null>(null);
  const cardRefs = useCallback((node: HTMLDivElement | null) => {
    cardRef.current = node;
    setCardNode(node);
  }, []);
  // Layout size, not the bounding rect: the card's zoom-in would report it scaled down.
  const [cardSize, setCardSize] = useState<Size>(CARD_ESTIMATE);
  useLayoutEffect(() => {
    if (!cardNode) return;
    const read = () => {
      const next = { width: cardNode.offsetWidth, height: cardNode.offsetHeight };
      setCardSize((prev) => (sameSize(prev, next) ? prev : next));
    };
    read();
    const observer = new ResizeObserver(read);
    observer.observe(cardNode);
    return () => observer.disconnect();
  }, [cardNode]);

  const liveRegion = (
    <div aria-live="polite" role="status" className="sr-only" data-tour-live>
      {tour.announcement}
    </div>
  );

  if (state.phase === 'paused') {
    return createPortal(
      <>
        {liveRegion}
        <PausedPill />
      </>,
      document.body,
    );
  }

  if (!step || !definition || waitingForBox) {
    return createPortal(liveRegion, document.body);
  }

  const placement =
    hole && !isMobile ? placeCard(hole, cardSize, viewport, preferredSide(step.placement)) : null;
  const sheetEdge =
    hole && hole.top > viewport.height - (hole.top + hole.height) ? 'top' : 'bottom';

  const card = (
    <StepCard
      key={`${definition.id}:${state.index}`}
      ref={cardRefs}
      kind={step.kind}
      title={title}
      body={body}
      index={state.index}
      total={counted}
      missingNote={state.missing ? t('tour.missing') : undefined}
      related={related}
      onRelated={(id) => {
        tour.next();
        window.setTimeout(() => void tour.start(id, { source: 'finish-card' }), 0);
      }}
      canGoBack={canGoBack}
      onNext={tour.next}
      onBack={tour.back}
      onEnd={() => tour.end('end-button')}
      reducedMotion={reducedMotion}
      sheet={isMobile}
      sheetEdge={sheetEdge}
      arrow={
        placement?.fits ? (
          <PopoverArrow asChild width={ARROW_WIDTH} height={ARROW_HEIGHT}>
            {/* Drawn pointing down; Radix rotates it. Lifted 1px to cover the card's border. */}
            <svg
              aria-hidden
              data-tour-arrow
              viewBox={`0 0 ${ARROW_WIDTH} ${ARROW_HEIGHT}`}
              className="fill-card stroke-border block -translate-y-px overflow-visible"
            >
              <path
                d={`M0 0 L${ARROW_WIDTH / 2} ${ARROW_HEIGHT} L${ARROW_WIDTH} 0 Z`}
                stroke="none"
              />
              <path d={`M0 0 L${ARROW_WIDTH / 2} ${ARROW_HEIGHT} L${ARROW_WIDTH} 0`} fill="none" />
            </svg>
          </PopoverArrow>
        ) : undefined
      }
    />
  );

  const isAction = step.kind === 'action' && !state.missing;

  const spotlight = hole ? (
    <div
      aria-hidden
      data-tour-spotlight
      className={cn(
        'ring-ring pointer-events-none fixed rounded-lg ring-2',
        LAYER_Z,
        !reducedMotion && SPOTLIGHT_MOTION,
        container ? '' : 'shadow-[0_0_0_9999px_rgb(0_0_0/0.5)]',
        !reducedMotion && 'animate-in fade-in-0 duration-200',
      )}
      style={hole}
    >
      {isAction ? (
        reducedMotion ? null : (
          <span
            data-tour-pulse
            className="ring-primary absolute inset-0 animate-ping rounded-lg ring-2 [animation-iteration-count:2] motion-reduce:hidden"
          />
        )
      ) : null}
    </div>
  ) : null;

  let shield = null;
  if (!container) {
    if (!hole) {
      shield = (
        <div
          aria-hidden
          data-tour-backdrop
          className={cn(
            'pointer-events-auto fixed inset-0 bg-black/50',
            LAYER_Z,
            !reducedMotion && 'animate-in fade-in-0 duration-200',
          )}
        />
      );
    } else if (isAction) {
      shield = <Blockers hole={hole} />;
    } else {
      shield = (
        <div
          aria-hidden
          data-tour-backdrop
          className={cn('pointer-events-auto fixed inset-0', LAYER_Z)}
        />
      );
    }
  }

  let placedCard;
  if (isMobile) {
    placedCard = (
      <div
        data-tour-sheet={sheetEdge}
        className={cn(
          'pointer-events-none fixed inset-x-4',
          sheetEdge === 'top' ? 'top-4' : 'bottom-4',
          CARD_Z,
        )}
      >
        {card}
      </div>
    );
  } else if (!placement) {
    placedCard = (
      <div
        className={cn(
          'pointer-events-none fixed inset-0 flex items-center justify-center p-4',
          CARD_Z,
        )}
      >
        {card}
      </div>
    );
  } else {
    placedCard = (
      <Popover open modal={false}>
        <PopoverAnchor virtualRef={virtualRef} />
        <PopoverBareContent
          asChild
          container={container}
          side={placement.side}
          sideOffset={placement.offset}
          collisionPadding={VIEWPORT_MARGIN}
          arrowPadding={ARROW_PADDING}
          updatePositionStrategy="always"
          className={cn(CARD_Z, 'origin-(--radix-popover-content-transform-origin)')}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            tour.end('escape');
          }}
        >
          {card}
        </PopoverBareContent>
      </Popover>
    );
  }

  const popover = !isMobile && hole !== null;
  // A sheet or centred card for a step inside an open dialog goes into the dialog, which makes
  // everything outside it inert.
  const cardHost = container ?? document.body;

  return (
    <>
      {createPortal(
        <>
          {liveRegion}
          {shield}
          {spotlight}
        </>,
        document.body,
      )}
      {popover ? placedCard : createPortal(placedCard, cardHost)}
    </>
  );
}
